import React, { JSX, useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
// import { ScrollView as GestureScrollView } from 'react-native-gesture-handler';
import { Platform, View, ScrollView as GestureScrollView, ViewProps, Pressable, Text } from 'react-native';
import { Svg, Defs, Pattern, Rect, Line } from 'react-native-svg';
import styles from '../../../stylesheet';
import { Ionicons } from '@expo/vector-icons';
import SearchComponent from './SearchComponent/SearchBar';
import { SearchComponentTypes } from './SearchComponent/Types';
import { debounce } from 'lodash';
import { useNavigation } from '@react-navigation/native';
import { useDispatch, useSelector } from 'react-redux';
import { useDeviceType } from '@hooks/useDeviceType';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { HelpRobotProps } from '@components/RobotHelper/SwipeableRobot';
import SwipeableRobot, { RobotHandle } from '@components/RobotHelper/SwipeableRobot';
import { DefaultRootState } from '@reduxLocal/persistState';
import ErrorBoundary from '@utils/ErrorBoundary';

export interface DefaultScreenHandle {
  showRobotHelp: () => void;
  hideRobot: () => void;
  getRobotRef: () => RobotHandle | null;
}

type ScreenViewProps = {
  navigation?: any;
  screenTitle?: String;
  children?: JSX.Element[] | JSX.Element;
  route?: any;
  upperLevelScreen?: boolean;
  backButton?: boolean;
  backButtonCallback?: Function;
  backButtonStyle?: ViewProps['style'];
  lightningActions?: boolean;
  noMargin?: boolean;
  scrollDisabled?: boolean;
  searchBar?: SearchComponentTypes;
  removeMargin?: boolean;
  searchBarStyle?: ViewProps['style'];
  aiCallback?: (text: string) => void;
  style?: ViewProps['style'];
  fetchingState?: boolean;
  inverseSearch?: boolean;
  containerStyle?: ViewProps['style'];
  awaitMenuClose?: boolean;
  aiSearch?: boolean;
  enableGrid?: boolean;
  gridSize?: number;
  transparentBackground?: boolean;
  robotHelperProps?: HelpRobotProps & {
    showByDefault?: boolean;
    autoShow?: boolean;
    position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  };
  gridColor?: string;
  gridOpacity?: number;
  fullWidth?: boolean;
  layoutCallback?: ({ height, width }: { height: number, width: number }) => void;
  dismissKeyboardOnTap?: boolean;
};

const DefaultScreen = forwardRef<DefaultScreenHandle, ScreenViewProps>(({
  layoutCallback,
  aiSearch,
  style,
  aiCallback,
  inverseSearch,
  upperLevelScreen,
  noMargin,
  screenTitle,
  children,
  transparentBackground,
  fullWidth,
  route,
  backButton,
  backButtonCallback,
  lightningActions,
  scrollDisabled,
  containerStyle,
  searchBarStyle,
  searchBar,
  awaitMenuClose,
  fetchingState = false,
  removeMargin,
  robotHelperProps,
  backButtonStyle,
  enableGrid = false,
  gridSize = 20,
  gridColor,
  gridOpacity = 0.1,
  dismissKeyboardOnTap = false,
  // @ts-ignore
  ...props
}: ScreenViewProps, ref) => {
  const [lastDimensions, setLastDimensions] = useState<{ height: number; width: number }>({
    height: 0,
    width: 0,
  });
  const { isTablet } = useDeviceType();
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const backAvailable = navigation.canGoBack();
  const theme = themeSelector();
  const robotRef = useRef<RobotHandle>(null);
  
  // Robot visibility state from Redux
  const robotVisible = useSelector((state: DefaultRootState) => state.UI?.robotVisible !== false);
  
  // Default robot help text if none provided
  const defaultRobotHelp = [
    'Welcome to SmartSaaS! I\'m here to help you navigate.',
    'Click on me to cycle through helpful tips.',
    'You can swipe me left to dismiss when you\'re done.',
  ];

  // Expose robot controls to parent components
  useImperativeHandle(ref, () => ({
    showRobotHelp: () => {
      robotRef.current?.showHelp();
    },
    hideRobot: () => {
      dispatch({ type: "HIDE_ROBOT" });
    },
    getRobotRef: () => robotRef.current,
  }), [dispatch]);

  // Auto-detect grid color based on theme if not provided
  const effectiveGridColor = gridColor || (theme ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.05)');

  // Robot positioning logic
  const getRobotPosition = (position: string = 'bottom-right') => {
    const baseOffset = {
      web: 20,
      ios: isTablet ? 40 : 80,
      android: 60
    };

    const sideOffset = {
      web: 20,
      ios: 20,
      android: 10
    };

    switch (position) {
      case 'bottom-left':
        return {
          bottom: Platform.select(baseOffset),
          left: Platform.select(sideOffset),
        };
      case 'top-right':
        return {
          top: Platform.select({ web: 80, ios: 100, android: 80 }),
          right: Platform.select(sideOffset),
        };
      case 'top-left':
        return {
          top: Platform.select({ web: 80, ios: 100, android: 80 }),
          left: Platform.select(sideOffset),
        };
      case 'bottom-right':
      default:
        return {
          bottom: Platform.select(baseOffset),
          right: Platform.select(sideOffset),
        };
    }
  };

  // Grid background component
  const GridBackground = () => {
    if (!enableGrid || lastDimensions.width === 0 || lastDimensions.height === 0) return null;

    return (
      <View style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: -1
      }}>
        <Svg
          width={lastDimensions.width}
          height={lastDimensions.height}
          style={{ position: 'absolute' }}
        >
          <Defs>
            <Pattern
              id="grid"
              width={gridSize}
              height={gridSize}
              patternUnits="userSpaceOnUse"
            >
              <Rect
                width={gridSize}
                height={gridSize}
                fill="transparent"
              />
              <Line
                x1="0"
                y1="0"
                x2="0"
                y2={gridSize}
                stroke={effectiveGridColor}
                strokeWidth="0.5"
                opacity={gridOpacity}
              />
              <Line
                x1="0"
                y1="0"
                x2={gridSize}
                y2="0"
                stroke={effectiveGridColor}
                strokeWidth="0.5"
                opacity={gridOpacity}
              />
            </Pattern>
          </Defs>
          <Rect
            width="100%"
            height="100%"
            fill="url(#grid)"
          />
        </Svg>
      </View>
    );
  };

  useEffect(() => {    
    // Auto-show robot if specified
    if (robotHelperProps?.autoShow && robotRef.current) {
      const timer = setTimeout(() => {
        robotRef.current?.showHelp();
      }, robotHelperProps.autoShow === true ? 2000 : 1000);
      
      return () => clearTimeout(timer);
    }
  }, [robotHelperProps?.autoShow]);

  const handleLayout = debounce((layout: { height: number; width: number }) => {
    if (layoutCallback) {
      const { height, width } = layout;
      if (height !== lastDimensions.height || width !== lastDimensions.width) {
        layoutCallback?.({ height, width });
        setLastDimensions({ height, width });
      }
     }
   }, 100); // Adjust the delay as needed

  return (
    <ErrorBoundary>
    <View
      style={[
        styles.pageContainer,
        {
          elevation: 20,
          flexGrow: 1,
          alignSelf: "stretch",
          minWidth: "100%",
          flex: 1,
          paddingVertical: Platform.select({ native: 10 }),
          paddingTop: Platform.select({ web: 0, default: isTablet && 60 }),
          maxWidth: !fullWidth && 1200,
          paddingHorizontal: !fullWidth ? Platform.select({ web: '2%' }) : undefined
          } as any,
        style,
      ]}
      onLayout={(e) => handleLayout({ height: e.nativeEvent.layout.height, width: e.nativeEvent.layout.width })}
    >
      <GridBackground />
      {searchBar && (
          <View style={{
            top: Platform.select({ web: 10, default: searchBar.aiCallback ? 30 : isTablet ? -40 : -10 }),
            marginBottom: Platform.select({ native: 40 }),
            marginTop: isTablet ? 10 : (removeMargin ? 0 : 10),
            zIndex: 2,
            // Center the search bar horizontally
            alignSelf: 'center',
            // Make search bar full width on tablet, but center it
            width: isTablet ? '95%' : '100%',
            // Add symmetric left margin for back button on tablet to maintain centering
            marginLeft: backButton && isTablet ? 40 : 0,
            marginRight: backButton && isTablet ? 40 : 0,
            // CRITICAL: Set a fixed height to prevent layout shifts when search bar expands
            // The SearchComponent is absolutely positioned and will overlay, but this wrapper
            // needs a fixed height so it doesn't expand and push content down
            height: searchBar.aiCallback ? (isTablet ? 56 : (Platform.OS === 'ios' ? 48 : 52)) : undefined,
            // Ensure overflow is visible so expanded search bar can overlay content
            overflow: 'visible' as const,
            // Position relative so absolutely positioned SearchComponent is relative to this
            position: 'relative' as const,
          }}>
          <SearchComponent
            aiSearch={aiSearch || false}
            placeHolder={aiSearch && !searchBar.placeHolder ? "Use AI to help you" : searchBar.placeHolder}
            inverse={inverseSearch}
            backButton={backButton}
            fetchingState={fetchingState}
            customStyle={searchBarStyle}
              aiCallback={searchBar.aiCallback || aiCallback}
            showSearch={searchBar.showSearch}
            key={searchBar.key}
            callback={searchBar.callback}
            searchState={searchBar.searchState}
            setSearchState={searchBar.setSearchState}
            customIcons={searchBar.customIcons}
            extraComponents={searchBar.extraComponents}
            otherComponents={searchBar.otherComponents}
              onlyOthers={searchBar.onlyOthers}
              fullWidthOnTablet={true}
          />
        </View>
      )}
      {
        backButton && Platform.OS !== 'web' && (
          <Pressable onPress={() => {
              if (backButtonCallback) {
                backButtonCallback()
              }
            if (upperLevelScreen || !backAvailable) {
              dispatch({ type: "OPEN_MENU" })
            } else {
              navigation.goBack()
            }
            }} style={[{ zIndex: 10, position: "absolute", padding: 5, top: Platform.select({ web: 50, default: isTablet ? 60 : 28 }), left: 0, borderTopStartRadius: 0, borderBottomStartRadius: 0, borderRadius: 20, backgroundColor: "rgb(90,192,234)" }, backButtonStyle]}>
            <Ionicons name="arrow-back" size={24} color="white" />
          </Pressable>
        )
      }
      {scrollDisabled ? (
        <View
          style={[
            styles.screen,
              {
                alignSelf: 'stretch',
                overflow: 'hidden',
                flex: 1,
                padding: noMargin ? 0 : 5,
                width: '100%',
              },
          ]}
          {...props}
        >
            {React.Children.toArray(children).map((child, index) => {
              // Filter out text nodes (strings, numbers, whitespace)
              if (child == null || typeof child === 'boolean') {
                return null;
              }
              if (typeof child === 'string' || typeof child === 'number') {
                // Filter out whitespace-only strings
                if (typeof child === 'string' && child.trim() === '') {
                  return null;
                }
                // Wrap text nodes in Text component
                return <Text key={index}>{child}</Text>;
              }
              return child;
            })}
        </View>
      ) : (
        <GestureScrollView
          showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps={dismissKeyboardOnTap ? "handled" : "never"}
              keyboardDismissMode={dismissKeyboardOnTap ? "on-drag" : "none"}
          style={[
            containerStyle,
            styles.screen,
            { flex: 1, paddingTop: removeMargin ? 0 : 30, minHeight: 40, padding: noMargin ? 0 : 5, width: '100%' } as any,
          ]}
              contentContainerStyle={{
                paddingVertical: Platform.select({ web: 20 }),
                flexGrow: 1,
                paddingBottom: 30,
                paddingHorizontal: 10,
                alignSelf: 'stretch',
                gap: 20,
                width: '100%',
                minWidth: '100%',
                overflow: 'visible',
                alignItems: 'stretch',
              } as any}
        >
              {React.Children.toArray(children).map((child, index) => {
                // Filter out text nodes (strings, numbers, whitespace)
                if (child == null || typeof child === 'boolean') {
                  return null;
                }
                if (typeof child === 'string' || typeof child === 'number') {
                  // Filter out whitespace-only strings
                  if (typeof child === 'string' && child.trim() === '') {
                    return null;
                  }
                  // Wrap text nodes in Text component
                  return <Text key={index}>{child}</Text>;
                }
                return child;
              })}
        </GestureScrollView>
      )}
      
      {/* Fixed Position SwipeableRobot */}
      {robotHelperProps && robotVisible && (
        <View style={{
          position: 'absolute',
          ...getRobotPosition(robotHelperProps.position),
          zIndex: 1000,
          elevation: 1000,
          pointerEvents: 'box-none', // Allow touches to pass through to underlying components
        }}>
          <SwipeableRobot
            ref={robotRef}
            textHelp={robotHelperProps.textHelp || defaultRobotHelp}
            videoId={robotHelperProps.videoId}
            removeSwipe={robotHelperProps.removeSwipe}
            showByDefault={robotHelperProps.showByDefault}
            dismissCallback={() => {
              robotHelperProps.dismissCallback?.();
              dispatch({ type: "HIDE_ROBOT" });
            }}
            style={{
              maxWidth: Platform.select({
                web: 400,
                default: isTablet ? 350 : 300
              }),
              transform: [{
                scale: Platform.select({
                  web: 0.9,
                  ios: isTablet ? 0.8 : 0.7,
                  android: 0.7
                })
              }],
              ...robotHelperProps.style
            }}
          />
        </View>
      )}
    </View>
    </ErrorBoundary>
  );
});

DefaultScreen.displayName = 'DefaultScreen';

export default DefaultScreen;
